/**
 * The few AS3 (AVM2) semantics that rule code needs when it ports game code line by line.
 *
 * They are copies of src/game/runtime/as3.ts helpers: the rules module must not import the runtime (it pulls in the
 * Flash runtime through the i18n bridge). tests/rules/infrastructure.test.ts checks both implementations agree.
 */

/**
 * AS3 `int(x)` and every assignment to an `int` slot, including `ProtectedInt.value = x` and `new ProtectedInt(x)`:
 * ToInt32(ToNumber(x)) - NaN/undefined -> 0, truncation toward zero, 32-bit wrap-around.
 */
export function int(v?: unknown): number {
  return (v as number) | 0;
}

/** AS3 `uint(x)`: ToUint32(ToNumber(x)). */
export function uint(v?: unknown): number {
  return (v as number) >>> 0;
}

function isIndexKey(k: string): boolean {
  return /^(0|[1-9][0-9]*)$/.test(k) && Number(k) < 4294967295;
}

/**
 * Keys enumerated by AS3 `for (k in o)` over plain data: array indices / integer-like names as numbers, everything
 * else as strings; order = own enumerable keys (indices ascending first, then insertion order). Use it wherever the
 * game iterates with `as3.keys(...)`: the iteration order decides e.g. which item a loop picks first.
 */
export function keys(o: unknown): (string | number)[] {
  if (o == null || (typeof o !== 'object' && typeof o !== 'function')) return [];
  return Object.keys(o).map((k) => (isIndexKey(k) ? Number(k) : k));
}

/** Values enumerated by AS3 `for each (v in o)` (same order as `keys`; array holes are skipped). */
export function values(o: unknown): unknown[] {
  if (o == null || (typeof o !== 'object' && typeof o !== 'function')) return [];
  return Object.keys(o).map((k) => (o as Record<string, unknown>)[k]);
}

/**
 * FWP.clone (AMF3 deep copy through a ByteArray, ported as as3.amfClone) for plain game data: arrays keep their
 * holes and extra named members, functions are dropped, shared references stay shared.
 */
export function clone<T>(v: T, seen: Map<unknown, unknown> = new Map()): T {
  if (v === null || v === undefined) return v;
  if (typeof v === 'function') return undefined as T;
  if (typeof v !== 'object') return v;
  if (seen.has(v)) return seen.get(v) as T;
  const src = v as Record<string, unknown>;
  const out: Record<string, unknown> = Array.isArray(v) ? ([] as unknown as Record<string, unknown>) : {};
  seen.set(v, out);
  for (const k of Object.keys(src)) {
    if (typeof src[k] === 'function') continue;
    out[k] = clone(src[k], seen);
  }
  return out as T;
}
